import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { UserRound, ChevronLeft, UserPlus, Check, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { SocialLinks } from "@/components/SocialLinks";
import { GiftPremiumModal } from "@/components/GiftPremiumModal";

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(false);
  const [gifting, setGifting] = useState(false);

  const load = () => {
    api.get(`/community/users/${id}`).then((res) => setProfile(res.data)).catch(() => setError(true));
  };
  useEffect(load, [id]);

  const request = async () => { await api.post("/community/request", { user_id: id }); toast.success("Friend request sent"); load(); };
  const accept = async () => { await api.post("/community/respond?accept=true", { user_id: id }); toast.success("Friend added"); load(); };

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 sm:px-8 pt-24 pb-24 text-center">
        <p className="font-decree text-xl text-[#4A4744]">This profile could not be found.</p>
        <Link to="/community" className="inline-block mt-6 px-6 py-3 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors">Back to community</Link>
      </div>
    );
  }
  if (!profile) return <div className="text-center pt-32"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#2C2A29]" /></div>;

  const status = profile.friend_status;

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="public-profile-page">
      <button
        data-testid="public-profile-back"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300 mb-10"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-col items-center text-center">
        <div className="w-28 h-28 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center">
          {profile.avatar_url ? (
            <img data-testid="public-avatar" src={mediaUrl(profile.avatar_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <UserRound className="w-12 h-12 text-[#B7AE9F]" />
          )}
        </div>
        <h1 data-testid="public-name" className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-6">{profile.name}</h1>

        {status === "friends" && <p className="font-body text-sm text-[#7A756E] mt-2">You are friends</p>}

        {(status === "friends" || status === "self") ? (
          <div className="mt-7">
            <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Connected accounts</p>
            <SocialLinks socials={profile.socials} iconClass="w-7 h-7" gap="gap-5" />
          </div>
        ) : (
          <p className="font-body text-sm text-[#7A756E] mt-7" data-testid="socials-hidden-note">
            Connect as friends to see their linked accounts.
          </p>
        )}

        {status === "none" && (
          <button data-testid="public-add-friend" onClick={request} className="mt-9 flex items-center gap-2 px-6 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
            <UserPlus className="w-4 h-4" /> Add friend
          </button>
        )}
        {status === "outgoing" && <span className="mt-9 font-body text-sm text-[#7A756E]">Friend request sent</span>}
        {status === "incoming" && (
          <button data-testid="public-accept-friend" onClick={accept} className="mt-9 flex items-center gap-2 px-6 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
            <Check className="w-4 h-4" /> Accept request
          </button>
        )}
        {status !== "self" && (
          <button
            data-testid="public-gift-premium"
            onClick={() => setGifting(true)}
            className="mt-4 flex items-center gap-2 px-6 py-3 border border-[#9B7E4E] text-[#9B7E4E] rounded-sm hover:bg-[#F3F0EA] transition-colors duration-300"
          >
            <Gift className="w-4 h-4" /> Gift a month of Premium
          </button>
        )}
      </div>
      {gifting && <GiftPremiumModal recipient={{ id, name: profile.name }} onClose={() => setGifting(false)} />}
    </div>
  );
}
